import { colors } from "../styles/variables";
import {
  FontNames,
  FontWeights,
  HexColor,
  TextTransform,
} from "../types/styles";

type themeNameTypes =
  | "Drips"
  | "Gatorade"
  | "PantryShop"
  | "PepDefault"
  | "PepsiCoShop"
  | "Poppi"
  | "SodaStream"
  | "TeamZone"
  | "Snacks";

export const themeNames: Record<themeNameTypes, string> = {
  Drips: "Drips",
  Gatorade: "Gatorade",
  PantryShop: "PantryShop",
  PepDefault: "Pep Default",
  PepsiCoShop: "PepsiCoShop",
  Poppi: "Poppi",
  SodaStream: "SodaStream",
  TeamZone: "Gatorade TeamZone",
  Snacks: "Snacks",
};

interface ButtonStyles {
  color: HexColor;
  textColor?: HexColor;
  hollow?: boolean;
  solidOnHover?: boolean;
  hollowOnHover?: boolean;
}

interface HeadingStyles {
  fontSize: {
    desktop: string;
    mobile?: string;
  };
  fontFamily?: FontNames;
  fontWeight?: FontWeights;
  letterSpacing?: string;
  lineHeight?: string;
  color?: HexColor;
  textTransform?: TextTransform;
}

export interface ThemeInterface {
  name: string;
  color: {
    primary: HexColor;
    secondary: HexColor;
    tertiary: HexColor;
    accent: HexColor;
    disabled: HexColor;
    action: HexColor;
    error: HexColor;
    text: HexColor;
    adaCompliantText?: HexColor;
  };
  button: {
    primary: ButtonStyles;
    secondary: ButtonStyles;
    tertiary: ButtonStyles;
    cancel: ButtonStyles;
    error: ButtonStyles;
    text: {
      color: HexColor;
    };
    disabled?: {
      hollow?: boolean;
    };
    fontFamily?: FontNames;
    fontWeight?: FontWeights;
    textTransform?: string;
    hasThinBorder?: boolean;
    borderRadius?: string;
    padding?: string;
  };
  typography: {
    body?: {
      fontSize?: string;
      fontFamily?: FontNames;
      fontWeight?: FontWeights;
      letterSpacing?: string;
      lineHeight?: string;
      color?: HexColor;
    };
    heading: {
      xxl: HeadingStyles;
      xl: HeadingStyles;
      l: HeadingStyles;
      m: HeadingStyles;
      s: HeadingStyles;
      xs: HeadingStyles;
      xxs: HeadingStyles;
    };
  };
  borderRadius?: string;
  page?: {
    backgroundColor?: HexColor;
  };
  header?: {
    fontFamily?: FontNames;
    fontSize?: string;
    fontWeight?: FontWeights;
    textTransform?: TextTransform;
    portal?: {
      backgroundColor?: HexColor;
      mobileLinkColor?: HexColor;
      navLinkColor?: HexColor;
      navLinkOpacity?: string;
      fontWeight?: FontWeights;
      textTransform?: string;
      fontSize?: string;
      borderBottomColor?: HexColor;
    };
    shop?: {
      backgroundColor?: HexColor;
      mobileLinkColor?: HexColor;
      navLinkColor?: HexColor;
      navLinkHover?: {
        color: HexColor;
        backgroundColor: HexColor;
      };
    };
    simple?: {
      checkout?: {
        backgroundColor?: HexColor;
      };
      portal?: {
        backgroundColor?: HexColor;
      };
    };
  };
  footer?: {
    backgroundColor?: HexColor;
    textColor?: HexColor;
    copyright?: {
      backgroundColor?: HexColor;
      textColor?: HexColor;
    };
  };
  alert?: {
    info?: {
      textColor?: HexColor;
    };
  };
  priceAccentColor?: HexColor;
  dividerColor?: HexColor;
  banner?: {
    backgroundColor?: HexColor;
  };
  cart?: {
    isFullWidth?: boolean;
    buttonFontSize?: string;
    sectionBackgroundColor?: HexColor;
    freeShipSection?: {
      progressBarColor?: HexColor;
      progressBarBackgroundColor?: HexColor;
    };
  };
  terms?: {
    login?: {
      // this array is a 1-1 mapping with the array in tenant.brandConfig.customTexts.body
      fontSize?: string[];
    };
    review?: {
      // this array is a 1-1 mapping with the array in tenant.brandConfig.customTexts.body
      fontSize?: string[];
    };
    signup?: {
      // this array is a 1-1 mapping with the array in tenant.brandConfig.customTexts.body
      fontSize?: string[];
    };
  };
}

export type ThemeType = ThemeInterface;

// PepDefaultTheme includes only the required
// fields from ThemeInterface. Also, DefaultTheme
// is already taken hence PepDefaultTheme naming
export const PepDefaultTheme: ThemeInterface = {
  name: themeNames.PepDefault,
  color: {
    primary: colors.gray600,
    secondary: colors.gray600,
    tertiary: colors.gray600,
    accent: colors.gray600,
    disabled: colors.gray300,
    action: colors.green,
    error: colors.red,
    text: colors.gray900,
  },
  button: {
    primary: { color: colors.gray600 },
    secondary: { color: colors.green },
    tertiary: { color: colors.gray600 },
    cancel: { color: colors.gray600 },
    error: { color: colors.red },
    text: { color: colors.gray600 },
  },
  typography: {
    heading: {
      xxl: {
        fontSize: {
          desktop: "46px",
        },
      },
      xl: {
        fontSize: {
          desktop: "36px",
        },
      },
      l: {
        fontSize: {
          desktop: "24px",
        },
      },
      m: {
        fontSize: {
          desktop: "18px",
        },
      },
      s: {
        fontSize: {
          desktop: "16px",
        },
      },
      xs: {
        fontSize: {
          desktop: "14px",
        },
      },
      xxs: {
        fontSize: {
          desktop: "14px",
        },
        fontWeight: "400",
      },
    },
  },
};
